// Documentos de compliance (componente 3.3, migration 0050).
//
// Um anexo com validade, ligado a um item de infraestrutura, a um alvará ou a
// um funcionário. Os três pontos de uso partilham a mesma tabela, então a
// pergunta "o que vence nos próximos 30 dias" tem uma consulta só, em vez de
// uma por tela.
//
// Quem decide o condomínio da linha é o banco (gatilho `documentos_condominio`):
// esta camada nem envia `condominio_id`, porque enviá-lo daria à tela a chance
// de escolher o Tenant — que é exatamente o que `.claude/rules/security.md`
// proíbe.

import { supabase } from '@/lib/supabase';
import { enviarDocumento, removerDocumento, urlDoDocumento } from '@/lib/condominioRepo';
import { periodoDeColunas, periodoParaColunas, type PeriodoParcial } from '@/lib/periodo';

export type TipoDeDocumento = 'treinamento' | 'alvara' | 'registro_profissional' | 'avulso';

/** A quem o documento pertence. Exatamente um, como a constraint exige. */
export type DonoDoDocumento =
  | { tipo: 'infraestrutura'; id: string }
  | { tipo: 'alvara'; id: string }
  | { tipo: 'funcionario'; id: string };

export interface Documento {
  id: string;
  condominioId: string;
  tipo: TipoDeDocumento;
  nome: string;
  descricao: string | null;
  numero: string | null;
  emitidoEm: string | null;
  /** Data exata do vencimento, quando foi ela que se informou. */
  validade: string | null;
  /** Ou a validade como Período contado da emissão. Nunca os dois. */
  validadePeriodo: PeriodoParcial;
  /** Apurado pelo banco a partir de um ou de outro. Só leitura. */
  venceEm: string | null;
  arquivoCaminho: string | null;
  arquivoNome: string | null;
  arquivoTamanho: number | null;
  profissaoId: string | null;
  /** Certificado: de qual treinamento ele e a prova (migration 0055). */
  treinamentoId: string | null;
  /** Anexo que satisfaz um documento esperado pela profissao (migration 0055). */
  profissaoDocumentoId: string | null;
  dono: DonoDoDocumento;
}

const COLUNAS =
  'id, condominio_id, tipo, nome, descricao, numero, emitido_em, validade, ' +
  'validade_valor, validade_unidade, vence_em, arquivo_caminho, arquivo_nome, ' +
  'arquivo_tamanho, profissao_id, treinamento_id, profissao_documento_id, ' +
  'infraestrutura_id, alvara_id, funcionario_id';

/**
 * Linha crua do PostgREST.
 *
 * O cliente do Supabase nao infere o tipo quando as colunas vem de uma
 * constante montada por concatenacao: ele cai em `GenericStringError`. O tipo
 * explicito abaixo e a alternativa a `any` — continua obrigando a converter
 * cada campo em `paraDocumento`, que e onde o formato de verdade e afirmado.
 */
type LinhaDeDocumento = Record<string, unknown>;

function paraDocumento(linha: LinhaDeDocumento): Documento {
  const dono: DonoDoDocumento = linha.infraestrutura_id
    ? { tipo: 'infraestrutura', id: linha.infraestrutura_id as string }
    : linha.alvara_id
      ? { tipo: 'alvara', id: linha.alvara_id as string }
      : { tipo: 'funcionario', id: linha.funcionario_id as string };

  return {
    id: linha.id as string,
    condominioId: linha.condominio_id as string,
    tipo: linha.tipo as TipoDeDocumento,
    nome: linha.nome as string,
    descricao: (linha.descricao as string) ?? null,
    numero: (linha.numero as string) ?? null,
    emitidoEm: (linha.emitido_em as string) ?? null,
    validade: (linha.validade as string) ?? null,
    validadePeriodo: periodoDeColunas(linha, 'validade'),
    venceEm: (linha.vence_em as string) ?? null,
    arquivoCaminho: (linha.arquivo_caminho as string) ?? null,
    arquivoNome: (linha.arquivo_nome as string) ?? null,
    arquivoTamanho: (linha.arquivo_tamanho as number) ?? null,
    profissaoId: (linha.profissao_id as string) ?? null,
    treinamentoId: (linha.treinamento_id as string) ?? null,
    profissaoDocumentoId: (linha.profissao_documento_id as string) ?? null,
    dono,
  };
}

const COLUNA_DO_DONO: Record<DonoDoDocumento['tipo'], string> = {
  infraestrutura: 'infraestrutura_id',
  alvara: 'alvara_id',
  funcionario: 'funcionario_id',
};

export async function listarDocumentos(dono: DonoDoDocumento): Promise<Documento[]> {
  const { data, error } = await supabase
    .from('documentos')
    .select(COLUNAS)
    .eq(COLUNA_DO_DONO[dono.tipo], dono.id)
    .order('criado_em', { ascending: false });

  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as LinhaDeDocumento[]).map(paraDocumento);
}

export interface EntradaDeDocumento {
  id?: string;
  dono: DonoDoDocumento;
  tipo: TipoDeDocumento;
  nome: string;
  descricao?: string | null;
  numero?: string | null;
  emitidoEm?: string | null;
  validade?: string | null;
  validadePeriodo?: PeriodoParcial;
  profissaoId?: string | null;
  treinamentoId?: string | null;
  profissaoDocumentoId?: string | null;
  arquivoCaminho?: string | null;
  arquivoNome?: string | null;
  arquivoTamanho?: number | null;
}

export async function gravarDocumento(entrada: EntradaDeDocumento): Promise<Documento> {
  // Data exata e Período são caminhos exclusivos — a constraint
  // `documentos_validade_check` recusa os dois juntos, e com razão: seriam dois
  // vencimentos para o mesmo papel. Aqui a data informada tem precedência, que
  // é o que o formulário mostra como escolhido.
  const usaData = !!entrada.validade;
  const periodo = usaData
    ? { valor: null, unidade: null }
    : (entrada.validadePeriodo ?? { valor: null, unidade: null });

  const linha: Record<string, unknown> = {
    tipo: entrada.tipo,
    nome: entrada.nome,
    descricao: entrada.descricao ?? null,
    numero: entrada.numero ?? null,
    emitido_em: entrada.emitidoEm || null,
    validade: entrada.validade || null,
    ...periodoParaColunas(periodo, 'validade'),
    profissao_id: entrada.profissaoId ?? null,
    treinamento_id: entrada.treinamentoId ?? null,
    profissao_documento_id: entrada.profissaoDocumentoId ?? null,
    arquivo_caminho: entrada.arquivoCaminho ?? null,
    arquivo_nome: entrada.arquivoNome ?? null,
    arquivo_tamanho: entrada.arquivoTamanho ?? null,
    infraestrutura_id: null,
    alvara_id: null,
    funcionario_id: null,
    editado_em: new Date().toISOString(),
  };
  linha[COLUNA_DO_DONO[entrada.dono.tipo]] = entrada.dono.id;

  // `condominio_id` não vai: o gatilho o deriva do dono. Mandar daqui seria
  // deixar o formulário escolher o Tenant.
  if (entrada.id) linha.id = entrada.id;

  const { data, error } = await supabase
    .from('documentos')
    .upsert(linha, { onConflict: 'id' })
    .select(COLUNAS)
    .single();

  if (error) throw new Error(error.message);
  return paraDocumento(data as unknown as LinhaDeDocumento);
}

/**
 * Anexa o arquivo a um documento que já existe.
 *
 * A ordem importa: o caminho no bucket é `<condominio>/<documento>/<arquivo>`, e
 * a policy do storage lê o condomínio da primeira pasta. Sem o documento
 * gravado antes não há id para compor o caminho — nem condomínio para a policy
 * conferir.
 */
export async function anexarArquivo(documento: Documento, arquivo: File): Promise<Documento> {
  const anexo = await enviarDocumento('documentos', documento.condominioId, documento.id, arquivo);

  // O arquivo anterior sai do bucket: substituir sem remover deixa o storage
  // crescendo com versões que nenhuma linha aponta e ninguém consegue listar.
  if (documento.arquivoCaminho) {
    await removerDocumento('documentos', documento.arquivoCaminho).catch(() => {});
  }

  const { data, error } = await supabase
    .from('documentos')
    .update({
      arquivo_caminho: anexo.caminho,
      arquivo_nome: anexo.nome,
      arquivo_tamanho: anexo.tamanho,
      editado_em: new Date().toISOString(),
    })
    .eq('id', documento.id)
    .select(COLUNAS)
    .single();

  if (error) throw new Error(error.message);
  return paraDocumento(data as unknown as LinhaDeDocumento);
}

/** URL assinada por uma hora. O bucket é privado; não há URL pública. */
export function urlDoArquivo(caminho: string): Promise<string | null> {
  return urlDoDocumento('documentos', caminho);
}

/**
 * Desativa o documento.
 *
 * `.delete()` vira `deletado_em` pelo gatilho da migration 0019 — o registro
 * continua recuperável. O arquivo no bucket **fica**: apagá-lo tornaria a
 * restauração impossível, e é justamente o conteúdo que o histórico de
 * conformidade precisa provar.
 */
export async function removerDocumentoLogicamente(id: string): Promise<void> {
  const { error } = await supabase.from('documentos').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Situação do documento hoje. Mesma escala de `vw_registros_profissionais`. */
export type SituacaoDocumento = 'ausente' | 'sem_validade' | 'vigente' | 'a_vencer' | 'vencido';

export function situacaoDoDocumento(
  documento: Documento | null | undefined,
  diasDeAlerta: number,
): SituacaoDocumento {
  if (!documento) return 'ausente';
  if (!documento.venceEm) return 'sem_validade';

  const [ano, mes, dia] = documento.venceEm.slice(0, 10).split('-').map(Number);
  const vence = new Date(ano, mes - 1, dia);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  if (vence.getTime() < hoje.getTime()) return 'vencido';

  const limite = new Date(hoje);
  limite.setDate(limite.getDate() + diasDeAlerta);
  return vence.getTime() <= limite.getTime() ? 'a_vencer' : 'vigente';
}

export const ROTULO_SITUACAO_DOCUMENTO: Record<SituacaoDocumento, string> = {
  ausente: 'Não anexado',
  sem_validade: 'Sem validade',
  vigente: 'Vigente',
  a_vencer: 'A vencer',
  vencido: 'Vencido',
};

export const CLASSE_SITUACAO_DOCUMENTO: Record<SituacaoDocumento, string> = {
  ausente: 'bg-background-100 text-foreground-600 border-background-300',
  sem_validade: 'bg-background-100 text-foreground-600 border-background-300',
  vigente: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  a_vencer: 'bg-amber-100 text-amber-900 border-amber-200',
  vencido: 'bg-red-100 text-red-900 border-red-200',
};
